/* The Overview chain as a real DAG (Ale rulings 15a–15c, 2026-09-03;
 * crossed-band lengthening 2026-09-03).
 *
 * When an asset is co-owned — held by two or more holders — the fan-bar
 * tree cannot draw it: it would place the asset under one parent and point
 * a chip at it from the other. The ruling is a real DAG: every entity and
 * every instrument drawn ONCE, at depth = the longest holder→issuer path
 * from the fund, with ONE wire per holder into a co-owned asset, each wire
 * carrying that holder's figure. Wires that cross other wires are gentle
 * curves (15a); the layout scales to any N (15c — no density cutoff, only
 * a much higher pixel bound).
 *
 * DE-CROWDING (Ale 2026-09-03): where the wires of a band CROSS or converge
 * — many holders into shared co-owned assets, or several instruments into
 * one issuer — the band is LENGTHENED: its vertical gap grows with the
 * number of crossing/converging pairs so the curves have room to separate
 * and read. Applied to BOTH bands, per depth: the holder→instrument
 * position band, and the instrument→issuer band (multi-issuer included). A
 * band with no crossings keeps the base gap. Deterministic and unit-tested.
 *
 * This module is PURE (its only import is the equally pure `chainTree`) so
 * the bare node runner can prove the geometry — see
 * tests/unit/coownedLayout.test.mjs. It lays out on a fixed pixel grid:
 * boxes are absolutely positioned HTML in the renderer, the wires an SVG
 * underlay whose paths this module builds. The figures (units, %, trades)
 * are the record's — the renderer overlays them by id — so the layout
 * needs only the structural `ChainSegment`. */

import { chainOf, entityDepthsOf, fundRootId, type ChainSegment } from './chainTree.js'

/* Pixel grid — one column per node at a level, one band per depth. Kept
 * here so the renderer, the SVG viewport and the tests share one source. */
export const DAG = {
  colW: 236,
  boxW: 210,
  entityH: 54,
  instrH: 46,
  posWire: 66, // the long amber leg, holder → instrument (base gap)
  insWire: 24, // the short blue leg, instrument → issuer (base gap)
  padTop: 10,
  padX: 20,
  figY: 6, // the figure block's foot sits this far above the instrument top
  /** Crossed-band lengthening (2026-09-03, dialled back after Ale's review):
   *  each crossing/converging pair of wires in a band adds `posK` / `insK`
   *  px to that band's gap, clamped at `posMax` / `insMax`. Deliberately
   *  SUBTLE — just enough room for the curves to read, not a big gap. */
  posK: 7,
  posMax: 150,
  insK: 5,
  insMax: 95,
  /** The much-higher hard cutoff (15c): past this the SVG would be
   *  unreasonably large. */
  maxPx: 24000,
  /* ── Packing and the per-column drop (D4, 2026-09-04) ────────────────
   * A real portfolio level — twelve target assets under one fund (atlas
   * 92) — cannot stand side by side at the full pitch: at 236 px a column
   * the drawing is 2,900 px wide and the card clipped it. Ale's ruling is
   * the answer: never a grid, LENGTHEN THE VERTICAL EDGE. A crowded level
   * PACKS to half the pitch and every column that would then overlap (or
   * whose position wire would cross) an earlier one takes the next row
   * down — its holder → instrument edge is a drop longer, so the boxes
   * never touch and the chain fits the card. */
  /** A level with more nodes than this packs its columns. */
  packAt: 5,
  /** The packed pitch — half a column, so two rows hold what one could
   *  not. */
  packW: 118,
  /** Clear air between two boxes that stand on the same row. */
  gutter: 8,
  /** One row of drop: how much lower a staggered column stands, i.e. how
   *  much longer its position edge runs. Sized so the dropped column's own
   *  FIGURE clears the row-0 instrument beside it as well as its boxes —
   *  an instrument box, a figure block with its trade chip, and air. */
  drop: 96,
  /** Air a packed level's figure keeps above the instrument it rides into
   *  — half the label block plus its trade chip. */
  figRide: 22,
  /** Clear air a staggered column's instrument keeps above the row-0
   *  issuer beside it — the instrument → issuer band grows by the level's
   *  drop plus this, so a dropped paper never lands on its neighbour. */
  dropClear: 26,
} as const

/** The base row height with no crossing anywhere — the floor a band never
 *  drops below. A crossed band is this plus its lengthening. */
export const DAG_ROW_H = DAG.entityH + DAG.posWire + DAG.instrH + DAG.insWire

export type DagBoxKind = 'entity' | 'instrument'

export interface DagBox {
  id: string
  kind: DagBoxKind
  name: string | null
  depth: number
  /** The box's centre / top-left in the SVG's coordinate space. */
  cx: number
  top: number
  w: number
  h: number
  /** For an instrument box: the issuer it points into (its blue wire). */
  issuerId?: string | null
}

export interface DagWire {
  kind: 'pos' | 'ins'
  /** The leg this wire draws (a position wire) — its identity for focus. */
  positionId?: string
  instrumentId: string | null
  holderId?: string
  issuerId: string | null
  fromX: number
  fromY: number
  toX: number
  toY: number
  /** A cubic-bezier `d` with vertical tangents at both ends — a gentle
   *  curve of the boxes' own corner feel, never an overlapping straight
   *  bar (15a). */
  path: string
  /** Where the figure (units · %, trade chip) sits — a point ON this wire's
   *  own curve (see `pointOnCurve`), so the label ties to its line and not
   *  to a shared row. The renderer centres the label block on it. */
  figX?: number
  figY?: number
  /** True when this wire is part of a crossing set (it tangles with ≥1
   *  other wire in its band). The renderer keeps NON-crossing labels shown
   *  always, and hides crossing labels until the wire is hovered (Ale
   *  2026-09-03 hybrid). */
  crosses?: boolean
}

export interface DagLayout {
  boxes: DagBox[]
  wires: DagWire[]
  width: number
  height: number
  /** True when the drawing exceeds the pixel bound (never silently
   *  collapses; 15c). */
  overflow: boolean
}

/** A cubic bezier from a box bottom to a box top, tangent vertical at both
 *  ends so it leaves and arrives straight down — the curve only bows where
 *  the two x differ, which is exactly where wires would otherwise cross. */
function curve(x1: number, y1: number, x2: number, y2: number): string {
  const dy = Math.max(24, (y2 - y1) * 0.5)
  return `M ${r(x1)} ${r(y1)} C ${r(x1)} ${r(y1 + dy)}, ${r(x2)} ${r(y2 - dy)}, ${r(x2)} ${r(y2)}`
}
const r = (n: number) => Math.round(n * 10) / 10

/** The point at parameter `t` on the same cubic the wire draws — so a label
 *  anchored here RIDES its own curve. Control points are vertical-tangent
 *  at both ends (P1 under P0, P2 above P3), exactly as `curve`. At the
 *  mid-band this is where crossing wires are furthest apart, so the label
 *  sits on its own line, not in a shared row where converging labels pile. */
export function pointOnCurve(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  t: number
): { x: number; y: number } {
  const dy = Math.max(24, (y2 - y1) * 0.5)
  const mt = 1 - t
  const a = mt * mt * mt
  const b = 3 * mt * mt * t
  const c = 3 * mt * t * t
  const d = t * t * t
  return {
    x: r(a * x1 + b * x1 + c * x2 + d * x2),
    y: r(a * y1 + b * (y1 + dy) + c * (y2 - dy) + d * y2),
  }
}

/** The point on a wire's own curve closest to its foot that still keeps
 *  `clear` px of air above the box it arrives at — where a figure rides on
 *  a PACKED level: the wires there all leave the same holder foot and only
 *  separate near the bottom, so a mid-band label would pile over the holder
 *  while a flush-bottom one would sit on the instrument. Deterministic: the
 *  curve is scanned from the foot upwards in fixed steps (D4, 2026-09-04). */
export function rideLow(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  clear: number
): { x: number; y: number } {
  for (let t = 0.94; t > 0.3; t -= 0.02) {
    const p = pointOnCurve(x1, y1, x2, y2, t)
    if (p.y <= y2 - clear) return p
  }
  return pointOnCurve(x1, y1, x2, y2, 0.5)
}

/** Count the crossing / converging pairs of a band's wires, by their x
 *  endpoints alone (y is what we are about to size). Two wires are
 *  "tangled" when their order at the top differs from their order at the
 *  bottom — a genuine crossing — or when they meet at the same target (a
 *  co-owned fan-in, or several instruments into one issuer). Wires that
 *  merely fan OUT from one shared source are not counted: they spread
 *  apart, they do not crowd. Deterministic; the de-crowding measure. */
interface Seg {
  from: number
  to: number
}

/** Two wires are "tangled" when their order at the top differs from their
 *  order at the bottom — a genuine crossing — or when they meet at the same
 *  target (a co-owned fan-in, or several instruments into one issuer).
 *  Wires that merely fan OUT from one shared source are not tangled: they
 *  spread apart, they do not crowd. */
function tangled(a: Seg, b: Seg): boolean {
  if (a.from === b.from && a.to === b.to) return false
  const crossing = a.from !== b.from && a.to !== b.to && (a.from - b.from) * (a.to - b.to) < 0
  const converging = a.to === b.to && a.from !== b.from
  return crossing || converging
}

/** Count the tangled pairs of a band's wires — the de-crowding measure that
 *  sizes how much the band is lengthened. Deterministic, by x alone. */
export function bandCrossings(segs: Seg[]): number {
  let n = 0
  for (let i = 0; i < segs.length; i++)
    for (let j = i + 1; j < segs.length; j++) if (tangled(segs[i], segs[j])) n++
  return n
}

/** For each wire in a band, whether it tangles with ANY other wire there —
 *  i.e. it is part of a crossing set. This is what splits the two label
 *  regimes (Ale 2026-09-03 hybrid): a wire that crosses nothing keeps its
 *  label shown always; a wire that crosses ≥1 other hides its label until
 *  hovered, so the crowded band reads on demand. */
function crossingFlags<T extends Seg>(band: T[]): boolean[] {
  return band.map((s, i) => band.some((o, j) => j !== i && tangled(s, o)))
}

/* ── Packing and the ruled per-column drop (D4, 2026-09-04) ─────────────
 * Ale 2026-09-03, decision 2: "when arrows CROSS, INCREASE the distance
 * between holder and instrument (lengthen the position line); same for
 * instrument → issuer including the multi-issuer case. Crossing is
 * resolved by longer vertical edges, NEVER a grid."
 *
 * A column is one node of a level — its instrument and the issuer under
 * it, which drop together so the short blue edge stays short and the long
 * amber one carries the whole drop. The rule below is deterministic in
 * column order: the FIRST column of a conflicting pair keeps its edge, the
 * LATER one takes the next free row. */

/** The x pitch of one level and whether it is packed. A level of more than
 *  `DAG.packAt` nodes packs to half a column — the boxes then overlap, and
 *  the drop rule below staggers them apart. */
export function levelPitch(n: number): { pitch: number; packed: boolean } {
  const packed = n > DAG.packAt
  return { pitch: packed ? DAG.packW : DAG.colW, packed }
}

/** One column of a level: where it stands, and the holder x of every
 *  position wire arriving into it. */
export interface Column {
  cx: number
  from: number[]
}

/** Do two columns conflict? Either their boxes would OVERLAP at this
 *  level's pitch, or a position wire into one would CROSS a position wire
 *  into the other WHILE the two stand crowded — closer than a full column.
 *  Both are the same crowding and the ruling answers both the same way.
 *
 *  The crossing arm is deliberately gated on proximity: a band whose wires
 *  cross at a FULL pitch is already de-crowded by lengthening the band as
 *  a whole (`bandCrossings`, shipped and ruled 3 Sep), so the ruled co-
 *  owned shape keeps the geometry Ale approved. The per-column drop is
 *  what the band gap cannot buy — room sideways — and it is exactly the
 *  case where the boxes themselves are on top of each other. */
function columnsConflict(a: Column, b: Column, boxW: number): boolean {
  const dx = Math.abs(a.cx - b.cx)
  if (dx < boxW + DAG.gutter) return true
  if (dx >= DAG.colW - 1) return false
  for (const fa of a.from) for (const fb of b.from) if ((fa - fb) * (a.cx - b.cx) < 0) return true
  return false
}

/** The ROW of every column: the lowest row on which it conflicts with no
 *  earlier column there. Row 0 keeps the base edge; row r runs r drops
 *  longer. Deterministic in column order — the later of a crossing pair is
 *  the one that moves (Ale 2026-09-03). */
export function columnRows(cols: Column[], boxW: number = DAG.boxW): number[] {
  const rows: number[] = []
  cols.forEach((c, i) => {
    let r = 0
    for (;;) {
      const clash = cols.some((o, j) => j < i && rows[j] === r && columnsConflict(o, c, boxW))
      if (!clash) break
      r++
    }
    rows.push(r)
  })
  return rows
}

/** Does this chain have a level too wide to draw as a fan-bar tree? The
 *  Overview routes such a chain to the canvas renderer, which places every
 *  box itself and can pack and stagger them. */
export function isWideChain<S extends ChainSegment>(input: S[]): boolean {
  const chain = chainOf(input as ChainSegment[])
  const root = fundRootId(chain)
  if (!root) return false
  const depths = entityDepthsOf(chain)
  const perDepth = new Map<number, Set<string>>()
  for (const s of chain) {
    if (!s.issuer_id) continue
    const d = depths.get(s.issuer_id)
    if (d === undefined) continue
    const set = perDepth.get(d) ?? new Set<string>()
    set.add(s.issuer_id)
    perDepth.set(d, set)
  }
  for (const set of perDepth.values()) if (set.size > DAG.packAt) return true
  return false
}

/** Lay the chain out as a DAG. Depths are the tree's longest-path depths;
 *  within a depth, entities keep first-seen chain order (stable). An
 *  instrument sits one band above its issuer, over the issuer's column
 *  (several instruments into one issuer spread around it); every holder's
 *  position wire curves from the holder box down into the instrument.
 *  Crossed bands are lengthened per depth (see `bandCrossings`). */
export function dagLayout(input: ChainSegment[]): DagLayout {
  const chain = chainOf(input as ChainSegment[])
  const root = fundRootId(chain)
  if (!root) return { boxes: [], wires: [], width: 0, height: 0, overflow: false }
  const depths = entityDepthsOf(chain)

  // entity id → name, first-seen order within its depth
  const nameOf = new Map<string, string | null>()
  nameOf.set(root, chain.find((s) => s.holder_id === root)?.holder_name ?? null)
  const orderByDepth = new Map<number, string[]>()
  const place = (id: string, name: string | null) => {
    if (!nameOf.has(id)) nameOf.set(id, name)
    const d = depths.get(id)
    if (d === undefined) return
    const row = orderByDepth.get(d) ?? []
    if (!row.includes(id)) {
      row.push(id)
      orderByDepth.set(d, row)
    }
  }
  place(root, nameOf.get(root) ?? null)
  for (const s of chain) {
    if (depths.has(s.holder_id)) place(s.holder_id, s.holder_name)
    if (s.issuer_id && depths.has(s.issuer_id)) place(s.issuer_id, s.issuer_name)
  }

  // ── x first: columns depend on no vertical gap, so all cx can be fixed
  // before we size the (crossing-dependent) bands. A level too wide to
  // stand side by side PACKS to half a pitch; the drop rule below then
  // staggers the overlapping columns apart. ─────────────────────────────
  const sortedDepths = [...orderByDepth.entries()].sort((a, b) => a[0] - b[0])
  const pitchOf = new Map<number, number>()
  for (const [d, row] of sortedDepths) pitchOf.set(d, levelPitch(row.length).pitch)
  const levelW = (n: number, pitch: number) => (n - 1) * pitch + DAG.boxW
  const width =
    DAG.padX * 2 +
    Math.max(
      DAG.boxW,
      ...sortedDepths.map(([d, row]) => levelW(row.length, pitchOf.get(d) ?? DAG.colW))
    )
  const centreX = (i: number, n: number, pitch: number) => {
    const start = DAG.padX + (width - DAG.padX * 2 - levelW(n, pitch)) / 2
    return start + i * pitch + DAG.boxW / 2
  }
  const entityCx = new Map<string, number>()
  for (const [d, row] of sortedDepths) {
    const pitch = pitchOf.get(d) ?? DAG.colW
    row.forEach((id, i) => entityCx.set(id, centreX(i, row.length, pitch)))
  }

  // instruments: distinct instrument id, its issuer, and the legs into it
  interface Ins {
    id: string
    name: string | null
    issuerId: string
    legs: ChainSegment[]
  }
  const insMap = new Map<string, Ins>()
  const insByIssuer = new Map<string, string[]>()
  for (const s of chain) {
    if (!s.instrument_id || !s.issuer_id) continue
    let ins = insMap.get(s.instrument_id)
    if (!ins) {
      ins = { id: s.instrument_id, name: s.instrument_name, issuerId: s.issuer_id, legs: [] }
      insMap.set(s.instrument_id, ins)
      const list = insByIssuer.get(s.issuer_id) ?? []
      list.push(s.instrument_id)
      insByIssuer.set(s.issuer_id, list)
    }
    ins.legs.push(s)
  }
  const insCx = new Map<string, number>()
  const insIssuer = new Map<string, string>()
  for (const [issuerId, insIds] of insByIssuer) {
    const cx0 = entityCx.get(issuerId) ?? 0
    const pitch = pitchOf.get(depths.get(issuerId) ?? 0) ?? DAG.colW
    const spread = insIds.length > 1 ? Math.min(pitch * 0.42, DAG.boxW * 0.55) : 0
    insIds.forEach((id, k) => {
      const cx = insIds.length === 1 ? cx0 : cx0 - spread + (spread * 2 * k) / (insIds.length - 1)
      insCx.set(id, cx)
      insIssuer.set(id, issuerId)
    })
  }

  // ── size each band by its crossings, per depth. The position band that
  // FEEDS depth d holds every holder→instrument wire whose instrument's
  // issuer sits at depth d; the instrument band AT depth d holds every
  // instrument→issuer wire into an issuer at depth d. ──────────────────
  const posSegs = new Map<number, (Seg & { id: string })[]>()
  const insSegs = new Map<number, (Seg & { id: string })[]>()
  for (const s of chain) {
    if (!s.instrument_id || !s.issuer_id) continue
    const d = depths.get(s.issuer_id)
    if (d === undefined) continue
    const hx = entityCx.get(s.holder_id)
    const ix = insCx.get(s.instrument_id)
    if (hx !== undefined && ix !== undefined) {
      const list = posSegs.get(d) ?? []
      list.push({ from: hx, to: ix, id: s.position_id })
      posSegs.set(d, list)
    }
  }
  for (const [id, issuerId] of insIssuer) {
    const d = depths.get(issuerId)
    if (d === undefined) continue
    const ix = insCx.get(id)
    const ex = entityCx.get(issuerId)
    if (ix !== undefined && ex !== undefined) {
      const list = insSegs.get(d) ?? []
      list.push({ from: ix, to: ex, id })
      insSegs.set(d, list)
    }
  }
  const posGap = (d: number) =>
    DAG.posWire + Math.min(DAG.posMax, DAG.posK * bandCrossings(posSegs.get(d) ?? []))
  /* The instrument → issuer band also carries the level's DROP: a dropped
   * column's paper sits `drop` lower, so the band must be at least that
   * much deeper for it to clear the row-0 issuer standing beside it. */
  const insGap = (d: number) => {
    const base = DAG.insWire + Math.min(DAG.insMax, DAG.insK * bandCrossings(insSegs.get(d) ?? []))
    const drop = levelDrop.get(d) ?? 0
    return drop > 0 ? Math.max(base, drop + DAG.dropClear) : base
  }

  // ── which wires are part of a crossing set (per band), keyed by id, so
  // the renderer can split the two label regimes (hybrid). ──────────────
  const posCross = new Map<string, boolean>()
  for (const band of posSegs.values())
    crossingFlags(band).forEach((f, i) => posCross.set(band[i].id, f))
  const insCross = new Map<string, boolean>()
  for (const band of insSegs.values())
    crossingFlags(band).forEach((f, i) => insCross.set(band[i].id, f))

  // ── the ruled per-column DROP: at each level, a column that would
  // overlap an earlier one (a packed level) or whose position wire would
  // cross an earlier column's takes the next row down — so ITS holder →
  // instrument edge is a drop longer and the two never touch. The column's
  // instrument drops with its issuer, so the short blue edge stays short
  // and the long amber one carries the whole lengthening (Ale 2026-09-03:
  // longer vertical edges, never a grid). ──────────────────────────────
  const dropOf = new Map<string, number>()
  const levelDrop = new Map<number, number>()
  for (const [d, row] of sortedDepths) {
    const cols: Column[] = row.map((id) => ({
      cx: entityCx.get(id) ?? 0,
      from: chain
        .filter((s) => s.issuer_id === id)
        .map((s) => entityCx.get(s.holder_id))
        .filter((x): x is number => x !== undefined),
    }))
    const rows = columnRows(cols)
    row.forEach((id, i) => dropOf.set(id, rows[i] * DAG.drop))
    levelDrop.set(d, Math.max(0, ...rows) * DAG.drop)
  }

  // ── y: entity band tops, cumulative down the depths. Between the entity
  // at d-1 and the entity at d sit its box, its level's own drop, the
  // (lengthened) position wire, the instrument box and the (lengthened)
  // instrument wire. ────────────────────────────────────────────────────
  const entityTop = new Map<string, number>()
  const bandTop = new Map<number, number>()
  let prevTop: number = DAG.padTop
  let prevDepth: number | null = null
  for (const [d] of sortedDepths) {
    const top =
      prevDepth === null
        ? DAG.padTop
        : prevTop +
          (levelDrop.get(prevDepth) ?? 0) +
          DAG.entityH +
          posGap(d) +
          DAG.instrH +
          insGap(d)
    bandTop.set(d, top)
    prevTop = top
    prevDepth = d
    for (const id of orderByDepth.get(d) ?? []) entityTop.set(id, top + (dropOf.get(id) ?? 0))
  }

  const boxes: DagBox[] = []
  for (const [d, row] of sortedDepths) {
    for (const id of row) {
      boxes.push({
        id,
        kind: 'entity',
        name: nameOf.get(id) ?? null,
        depth: d,
        cx: entityCx.get(id) ?? 0,
        top: entityTop.get(id) ?? bandTop.get(d) ?? DAG.padTop,
        w: DAG.boxW,
        h: DAG.entityH,
      })
    }
  }

  const wires: DagWire[] = []
  const insTop = new Map<string, number>()
  for (const [issuerId, insIds] of insByIssuer) {
    const issuerDepth = depths.get(issuerId)
    if (issuerDepth === undefined) continue
    const cx0 = entityCx.get(issuerId) ?? 0
    // the instrument rides its issuer's own drop, so the blue edge stays
    // short and the amber one carries the lengthening
    const eTop = entityTop.get(issuerId) ?? bandTop.get(issuerDepth) ?? 0
    const top = eTop - insGap(issuerDepth) - DAG.instrH
    for (const id of insIds) {
      const cx = insCx.get(id) ?? cx0
      insTop.set(id, top)
      const ins = insMap.get(id)!
      boxes.push({
        id,
        kind: 'instrument',
        name: ins.name,
        depth: issuerDepth,
        cx,
        top,
        w: DAG.boxW,
        h: DAG.instrH,
        issuerId,
      })
      // the short blue instrument → issuer wire; its trade chip rides the
      // curve's midpoint so it ties to THIS paper, not a shared row
      const bx = cx
      const by = top + DAG.instrH
      const ty = eTop
      const anchor = pointOnCurve(bx, by, cx0, ty, 0.5)
      wires.push({
        kind: 'ins',
        instrumentId: id,
        issuerId,
        fromX: bx,
        fromY: by,
        toX: cx0,
        toY: ty,
        path: curve(bx, by, cx0, ty),
        figX: anchor.x,
        figY: anchor.y,
        crosses: insCross.get(id) ?? false,
      })
    }
  }

  // the long amber position wires — one per leg, holder box → instrument.
  // The figure RIDES its own curve near mid-band, where crossing wires are
  // furthest apart, so each qty·% (and its trade chip) ties unambiguously
  // to ITS line instead of stacking in a shared row near the instrument.
  // A small per-wire stagger of the ride point keeps neighbours from
  // touching (0.42 / 0.50 / 0.58 of the way down, cycling).
  let posIdx = 0
  for (const s of chain) {
    if (!s.instrument_id) continue
    const ix = insCx.get(s.instrument_id)
    const iTop = insTop.get(s.instrument_id)
    const hx = entityCx.get(s.holder_id)
    const hTop = entityTop.get(s.holder_id)
    if (ix === undefined || iTop === undefined || hx === undefined || hTop === undefined) continue
    const fromY = hTop + DAG.entityH
    /* Where the figure rides its wire. Mid-band normally — that is where
     * crossing wires are furthest apart. On a PACKED level it is the other
     * way round: a dozen wires leave the same holder foot and only separate
     * near the bottom, so mid-band would pile every label over the holder
     * box. There the figure rides LOW, each over its own column, alternating
     * with the column's own drop (D4, 2026-09-04). */
    const packedTarget = pitchOf.get(depths.get(s.issuer_id ?? '') ?? -1) === DAG.packW
    const t = 0.5 + 0.08 * ((posIdx % 3) - 1)
    posIdx++
    const anchor = packedTarget
      ? rideLow(hx, fromY, ix, iTop, DAG.figRide)
      : pointOnCurve(hx, fromY, ix, iTop, t)
    wires.push({
      kind: 'pos',
      positionId: s.position_id,
      instrumentId: s.instrument_id,
      holderId: s.holder_id,
      issuerId: s.issuer_id,
      fromX: hx,
      fromY,
      toX: ix,
      toY: iTop,
      path: curve(hx, fromY, ix, iTop),
      figX: anchor.x,
      figY: anchor.y,
      crosses: posCross.get(s.position_id) ?? false,
    })
  }

  const maxTop = Math.max(DAG.padTop, ...[...entityTop.values()])
  const height = maxTop + DAG.entityH + 8
  const overflow = width > DAG.maxPx || height > DAG.maxPx
  return { boxes, wires, width, height, overflow }
}
